/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import {
    StoreDetailsDto,
    StoreCashBoxesListFilterParams,
    StoreCoordinatesHistoryItemDto,
    StorePhotoInfoDto,
    StoreInspectionDto,
    StoreCashboxesListDto as StoreCashBoxesListDto,
    StoreIntegrationsData,
    StoreCashBoxesIntegrationData,
} from '../api/stores/storesClientTypings';
import {
    AiClassificationsHistoryDto,
    EntirePeriodTurnover,
    PageQueryParams,
    PeriodQueryParams,
    RegistryTurnoversByCashBoxesDto,
} from '../api/apiCommon.typings';
import { CashBoxesStatus, CashBoxesStats } from '../api/cashBoxes/cashBoxesClient.typings';
import { StoreDetailsActions, StoreDetailsActionType } from '../actions/storeDetailsActions';
import {
    collapseEntirePeriodTurnovers,
    expandTurnoversPeriod,
    updateEntirePeriodTurnoverWithNewData,
} from '../../components/RegistryWidgets/Common/TurnoversUtils';
import { today } from './CashBoxRegistry/cashboxDetailsReducer';
import moment from 'moment';
import { TurnoverReportType } from '../../components/RegistryWidgets/Common/ListTypes';

export const rowsPerPage = [20, 30, 40];
export const defaultPaginationLimit = rowsPerPage[0];
export const defaultCashBoxesStatusFilter: CashBoxesStatus[] = [
    'REGISTRATION',
    'ACTIVE',
    'BLOCKED',
    'RESET',
    'BROKEN',
    'SERVICE',
];

export interface StoreDetailsState {
    storeTaxNumber: string;
    storeDetails: StoreDetailsDto;
    storeCashBoxesStats: CashBoxesStats;
    coordinatesHistory: StoreCoordinatesHistoryItemDto[];
    inspectionsHistory: StoreInspectionDto[];

    storeCashBoxesList: StoreCashBoxesListDto;
    cashBoxesPageParams: PageQueryParams;
    cashBoxesFilterParams: StoreCashBoxesListFilterParams;
    isLoadingCashBoxesList: boolean;

    isLoading: boolean;
    isLoadingCashBoxesStats: boolean;
    isLoadingCoordinatesHistory: boolean;
    isLoadingInspectionsHistory: boolean;
    currentHistoryTab: number;

    storeTurnoversByPeriod: EntirePeriodTurnover;
    storeTurnoversByCashBoxes: RegistryTurnoversByCashBoxesDto;
    turnoversByCashBoxesPageParams: PageQueryParams;
    turnoversByCashBoxesFilterParams: PeriodQueryParams;
    isLoadingTurnovers: boolean;
    isTurnoversTableHidden: boolean;
    turnoversReportType: TurnoverReportType;

    isOpenDropZone: boolean;
    storePhotosInfo: StorePhotoInfoDto[];
    isLoadingStorePhotosInfo: boolean;
    isLoadingSetOnPreview: boolean;
    isLoadingUploadPhotos: boolean;
    isLoadingDeletePhotos: boolean;

    isLoadingAiClassificationsHistory: boolean;
    isDownloadingAiClassificationsHistory: boolean;
    aiClassificationsHistory: AiClassificationsHistoryDto;

    storeCashBoxesIntegrationPageParams: PageQueryParams;
    storeCashBoxesIntegrationList: StoreCashBoxesIntegrationData;
    isLoadingCashBoxesIntegrationList: boolean;
    storeIntegrationsData: StoreIntegrationsData;
    isLoadingIntegrationsTab: boolean;
}

const initialState: StoreDetailsState = {
    storeTaxNumber: null,
    storeDetails: null,
    storeCashBoxesStats: null,
    coordinatesHistory: null,
    inspectionsHistory: null,

    cashBoxesPageParams: {
        limit: defaultPaginationLimit,
        offset: 0,
    },
    cashBoxesFilterParams: {
        status: defaultCashBoxesStatusFilter,
    },
    storeCashBoxesList: null,
    isLoadingCashBoxesList: false,

    isLoading: false,
    isLoadingCashBoxesStats: false,
    isLoadingCoordinatesHistory: false,
    isLoadingInspectionsHistory: false,
    currentHistoryTab: 0,

    storeTurnoversByPeriod: null,
    storeTurnoversByCashBoxes: null,
    turnoversByCashBoxesPageParams: {
        limit: defaultPaginationLimit,
        offset: 0,
    },
    turnoversByCashBoxesFilterParams: {
        dateFrom: today.utc().clone().subtract(30, 'day').format(),
        dateTo: moment(new Date()).endOf('day').utc().format(),
    },
    isLoadingTurnovers: false,
    isTurnoversTableHidden: true,
    turnoversReportType: TurnoverReportType.ByPeriod,

    isOpenDropZone: false,
    storePhotosInfo: null,
    isLoadingStorePhotosInfo: false,
    isLoadingSetOnPreview: false,
    isLoadingUploadPhotos: false,
    isLoadingDeletePhotos: false,

    isLoadingAiClassificationsHistory: false,
    isDownloadingAiClassificationsHistory: false,
    aiClassificationsHistory: null,

    storeCashBoxesIntegrationPageParams: {
        limit: defaultPaginationLimit,
        offset: 0,
    },
    storeCashBoxesIntegrationList: null,
    isLoadingCashBoxesIntegrationList: false,
    storeIntegrationsData: null,
    isLoadingIntegrationsTab: false,
};

function storeDetailsReducer(state: StoreDetailsState = initialState, action: StoreDetailsActions): StoreDetailsState {
    switch (action.type) {
        case StoreDetailsActionType.OPEN_REGISTRY_STORE_CARD_PAGE:
            const previousTaxNumber = state.storeTaxNumber;
            const currentTaxNumber = action.payload;

            let isNewCardOpen = true;
            if (previousTaxNumber && currentTaxNumber) isNewCardOpen = previousTaxNumber !== currentTaxNumber;

            return {
                ...state,
                isLoading: true,
                isLoadingCashBoxesList: true,
                storeTaxNumber: action.payload,
                cashBoxesPageParams: isNewCardOpen
                    ? initialState.cashBoxesPageParams
                    : { ...state.cashBoxesPageParams },
                cashBoxesFilterParams: isNewCardOpen
                    ? initialState.cashBoxesFilterParams
                    : { ...state.cashBoxesFilterParams },

                storeTurnoversByPeriod:
                    isNewCardOpen || !state.storeTurnoversByPeriod ? null : { ...state.storeTurnoversByPeriod },
                storeTurnoversByCashBoxes:
                    isNewCardOpen || !state.storeTurnoversByCashBoxes ? null : { ...state.storeTurnoversByCashBoxes },
                turnoversByCashBoxesPageParams: isNewCardOpen
                    ? initialState.turnoversByCashBoxesPageParams
                    : { ...state.turnoversByCashBoxesPageParams },
                turnoversByCashBoxesFilterParams: isNewCardOpen
                    ? initialState.turnoversByCashBoxesFilterParams
                    : { ...state.turnoversByCashBoxesFilterParams },
                isTurnoversTableHidden: isNewCardOpen
                    ? initialState.isTurnoversTableHidden
                    : state.isTurnoversTableHidden,
                turnoversReportType: isNewCardOpen ? initialState.turnoversReportType : state.turnoversReportType,
            };

        case StoreDetailsActionType.APPLY_CASHBOXES_PAGING:
            return {
                ...state,
                isLoadingCashBoxesList: true,
                cashBoxesPageParams: action.payload,
            };

        case StoreDetailsActionType.APPLY_CASHBOXES_FILTER:
            return {
                ...state,
                isLoadingCashBoxesList: true,
                cashBoxesFilterParams: action.payload,
                cashBoxesPageParams: { ...state.cashBoxesPageParams, offset: 0 },
            };

        case StoreDetailsActionType.FETCH_STORE_CASHBOXES_LIST_SUCCESS:
            return {
                ...state,
                isLoadingCashBoxesList: false,
                storeCashBoxesList: action.payload,
            };

        case StoreDetailsActionType.APPLY_STORE_CASHBOXES_INTEGRATION_PAGING:
            return {
                ...state,
                isLoadingCashBoxesIntegrationList: true,
                storeCashBoxesIntegrationPageParams: action.payload,
            };

        case StoreDetailsActionType.FETCH_STORE_CASHBOXES_INTEGRATION_DATA_SUCCESS:
            return {
                ...state,
                isLoadingCashBoxesIntegrationList: false,
                storeCashBoxesIntegrationList: action.payload,
            };

        case StoreDetailsActionType.FETCH_STORE_CASHBOXES_INTEGRATION_DATA_ERROR:
            return {
                ...state,
                isLoadingCashBoxesIntegrationList: false,
                storeCashBoxesIntegrationList: {
                    count: 0,
                    cashboxes: [],
                },
            };

        case StoreDetailsActionType.FETCH_STORE_INTEGRATION_DATA_START:
            return {
                ...state,
                isLoadingIntegrationsTab: true,
            };

        case StoreDetailsActionType.FETCH_STORE_INTEGRATION_DATA_SUCCESS:
            return {
                ...state,
                isLoadingIntegrationsTab: false,
                storeIntegrationsData: action.payload,
            };

        case StoreDetailsActionType.FETCH_STORE_INTEGRATION_DATA_ERROR:
            return {
                ...state,
                isLoadingIntegrationsTab: false,
                storeIntegrationsData: null,
            };

        case StoreDetailsActionType.UPDATE_STORE_INTEGRATION_STATUS_SUCCESS:
            return {
                ...state,
                storeIntegrationsData: action.payload,
            };

        case StoreDetailsActionType.UPDATE_STORE_DETAILS:
            return {
                ...state,
                isLoading: true,
            };

        case StoreDetailsActionType.FETCH_STORE_DETAILS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                storeDetails: action.payload,
            };

        case StoreDetailsActionType.UPDATE_STORE_DETAILS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                storeDetails: {
                    ...state.storeDetails,
                    lat: action.payload.lat,
                    lng: action.payload.lng,
                },
            };

        case StoreDetailsActionType.FETCH_STORE_CARD_DATA_ERROR:
            return {
                ...state,
                isLoading: false,
                isLoadingCashBoxesList: false,
                storeCashBoxesList: {
                    count: 0,
                    cashboxes: [],
                },
            };

        case StoreDetailsActionType.CHANGE_HISTORY_TAB:
            return {
                ...state,
                currentHistoryTab: action.payload,
            };

        case StoreDetailsActionType.FETCH_STORE_CASHBOXES_STATS:
            return {
                ...state,
                isLoadingCashBoxesStats: true,
            };

        case StoreDetailsActionType.FETCH_STORE_CASHBOXES_STATS_SUCCESS:
            return {
                ...state,
                isLoadingCashBoxesStats: false,
                storeCashBoxesStats: action.payload,
            };

        case StoreDetailsActionType.FETCH_STORE_CASHBOXES_STATS_ERROR:
            return {
                ...state,
                isLoadingCashBoxesStats: false,
                storeCashBoxesStats: null,
            };

        case StoreDetailsActionType.FETCH_COORDINATES_HISTORY:
            return {
                ...state,
                isLoadingCoordinatesHistory: true,
            };

        case StoreDetailsActionType.FETCH_COORDINATES_HISTORY_SUCCESS:
            return {
                ...state,
                isLoadingCoordinatesHistory: false,
                coordinatesHistory: action.payload.items,
            };

        case StoreDetailsActionType.FETCH_COORDINATES_HISTORY_ERROR:
            return {
                ...state,
                isLoadingCoordinatesHistory: false,
                coordinatesHistory: [],
            };

        case StoreDetailsActionType.DOWNLOAD_COORDINATES_HISTORY:
            return {
                ...state,
                isLoadingCoordinatesHistory: true,
            };

        case StoreDetailsActionType.DOWNLOAD_COORDINATES_HISTORY_SUCCESS:
        case StoreDetailsActionType.DOWNLOAD_COORDINATES_HISTORY_ERROR:
            return {
                ...state,
                isLoadingCoordinatesHistory: false,
            };

        case StoreDetailsActionType.FETCH_INSPECTIONS_HISTORY:
        case StoreDetailsActionType.CREATE_INSPECTION:
        case StoreDetailsActionType.CANCEL_INSPECTION:
        case StoreDetailsActionType.ASSIGN_INSPECTION:
        case StoreDetailsActionType.COMPLETE_INSPECTION:
            return {
                ...state,
                isLoadingInspectionsHistory: true,
            };

        case StoreDetailsActionType.FETCH_INSPECTIONS_HISTORY_SUCCESS:
            return {
                ...state,
                isLoadingInspectionsHistory: false,
                inspectionsHistory: action.payload.items,
            };

        case StoreDetailsActionType.FETCH_INSPECTIONS_HISTORY_ERROR:
            return {
                ...state,
                isLoadingInspectionsHistory: false,
                inspectionsHistory: [],
            };

        case StoreDetailsActionType.CREATE_INSPECTION_SUCCESS:
            return {
                ...state,
                isLoadingInspectionsHistory: false,
                inspectionsHistory: [action.payload, ...state.inspectionsHistory],
            };

        case StoreDetailsActionType.CANCEL_INSPECTION_SUCCESS: {
            const data = [...state.inspectionsHistory];
            const deletedItemIndex = data.findIndex((item) => item.inspectionId === action.payload);
            if (deletedItemIndex !== -1) data.splice(deletedItemIndex, 1);
            return {
                ...state,
                isLoadingInspectionsHistory: false,
                inspectionsHistory: data,
            };
        }

        case StoreDetailsActionType.ASSIGN_INSPECTION_SUCCESS:
        case StoreDetailsActionType.COMPLETE_INSPECTION_SUCCESS: {
            const history = [...state.inspectionsHistory];
            const itemIndex = history.findIndex((item) => item.inspectionId === action.payload.inspectionId);
            if (itemIndex !== -1) {
                const isExpanded = history[itemIndex].isExpanded;
                history.splice(itemIndex, 1, action.payload);
                history[itemIndex].isExpanded = isExpanded;
            }
            return {
                ...state,
                isLoadingInspectionsHistory: false,
                inspectionsHistory: history,
            };
        }

        case StoreDetailsActionType.CREATE_INSPECTION_ERROR:
        case StoreDetailsActionType.CANCEL_INSPECTION_ERROR:
        case StoreDetailsActionType.ASSIGN_INSPECTION_ERROR:
        case StoreDetailsActionType.COMPLETE_INSPECTION_ERROR:
            return {
                ...state,
                isLoadingInspectionsHistory: false,
            };

        case StoreDetailsActionType.CHANGE_INSPECTION_VIEW: {
            const history = [...state.inspectionsHistory];
            const itemIndex = history.findIndex((item) => item.inspectionId === action.payload.inspectionId);
            if (itemIndex !== -1) history[itemIndex].isExpanded = action.payload.isExpanded;
            return {
                ...state,
                isLoadingInspectionsHistory: false,
                inspectionsHistory: history,
            };
        }

        case StoreDetailsActionType.COLLAPSE_ALL_INSPECTIONS_VIEWS: {
            const history = [...state.inspectionsHistory];
            for (let i = 0; i < history.length; i++) {
                history[i].isExpanded = false;
            }
            return {
                ...state,
                isLoadingInspectionsHistory: false,
                inspectionsHistory: history,
            };
        }

        case StoreDetailsActionType.SET_TURNOVERS_HIDE_TOGGLE:
            return {
                ...state,
                isTurnoversTableHidden: action.payload,
            };

        case StoreDetailsActionType.SET_TURNOVERS_REPORT_TYPE:
            return {
                ...state,
                turnoversReportType: action.payload,
            };

        case StoreDetailsActionType.FETCH_STORE_TURNOVERS_BY_PERIOD:
            return {
                ...state,
                isLoadingTurnovers: true,
            };

        case StoreDetailsActionType.FETCH_STORE_TURNOVERS_BY_PERIOD_SUCCESS:
            return {
                ...state,
                isLoadingTurnovers: false,
                storeTurnoversByPeriod: updateEntirePeriodTurnoverWithNewData(
                    state.storeTurnoversByPeriod,
                    action.payload,
                ),
            };

        case StoreDetailsActionType.FETCH_STORE_TURNOVERS_BY_PERIOD_ERROR:
            return {
                ...state,
                isLoadingTurnovers: false,
            };

        case StoreDetailsActionType.COLLAPSE_TURNOVERS_BY_PERIOD:
            return {
                ...state,
                storeTurnoversByPeriod: collapseEntirePeriodTurnovers(state.storeTurnoversByPeriod),
            };

        case StoreDetailsActionType.EXPAND_TURNOVERS_PERIOD:
            return {
                ...state,
                storeTurnoversByPeriod: expandTurnoversPeriod(
                    state.storeTurnoversByPeriod,
                    action.payload.period,
                    action.payload.isToHide,
                ),
            };

        case StoreDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD:
            return {
                ...state,
                isLoadingTurnovers: true,
            };

        case StoreDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD_SUCCESS:
        case StoreDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD_ERROR:
            return {
                ...state,
                isLoadingTurnovers: false,
            };

        case StoreDetailsActionType.FETCH_STORE_TURNOVERS_BY_CASHBOXES:
            return {
                ...state,
                isLoadingTurnovers: true,
            };

        case StoreDetailsActionType.FETCH_STORE_TURNOVERS_BY_CASHBOXES_SUCCESS:
            return {
                ...state,
                isLoadingTurnovers: false,
                storeTurnoversByCashBoxes: action.payload,
            };

        case StoreDetailsActionType.FETCH_STORE_TURNOVERS_BY_CASHBOXES_ERROR:
            return {
                ...state,
                isLoadingTurnovers: false,
                storeTurnoversByCashBoxes: {
                    count: 0,
                    turnovers: [],
                },
            };

        case StoreDetailsActionType.APPLY_TURNOVERS_BY_CASHBOXES_PAGING:
            return {
                ...state,
                isLoadingTurnovers: true,
                turnoversByCashBoxesPageParams: action.payload,
            };

        case StoreDetailsActionType.APPLY_TURNOVERS_BY_CASHBOXES_FILTER:
            return {
                ...state,
                isLoadingTurnovers: true,
                turnoversByCashBoxesFilterParams: action.payload,
                turnoversByCashBoxesPageParams: { ...state.turnoversByCashBoxesPageParams, offset: 0 },
            };

        case StoreDetailsActionType.DOWNLOAD_TURNOVERS_BY_CASHBOXES:
            return {
                ...state,
                isLoadingTurnovers: true,
            };

        case StoreDetailsActionType.DOWNLOAD_TURNOVERS_BY_CASHBOXES_SUCCESS:
        case StoreDetailsActionType.DOWNLOAD_TURNOVERS_BY_CASHBOXES_ERROR:
            return {
                ...state,
                isLoadingTurnovers: false,
            };

        case StoreDetailsActionType.FETCH_STORE_PHOTOS_INFO:
            return {
                ...state,
                isLoadingStorePhotosInfo: true,
            };

        case StoreDetailsActionType.FETCH_STORE_PHOTOS_INFO_SUCCESS:
            return {
                ...state,
                isLoadingStorePhotosInfo: false,
                storePhotosInfo: action.payload,
            };

        case StoreDetailsActionType.FETCH_STORE_PHOTOS_INFO_ERROR:
            return {
                ...state,
                isLoadingStorePhotosInfo: false,
            };

        case StoreDetailsActionType.SET_STORE_PHOTO_ON_PREVIEW:
            return {
                ...state,
                isLoadingSetOnPreview: true,
            };

        case StoreDetailsActionType.SET_STORE_PHOTO_ON_PREVIEW_SUCCESS:
            return {
                ...state,
                isLoadingSetOnPreview: false,
                storePhotosInfo: state.storePhotosInfo.map((photo) => {
                    if (photo.name !== action.payload && photo.preview) photo.preview = false;
                    if (photo.name === action.payload) photo.preview = true;

                    return photo;
                }),
            };

        case StoreDetailsActionType.SET_STORE_PHOTO_ON_PREVIEW_ERROR:
            return {
                ...state,
                isLoadingSetOnPreview: false,
            };

        case StoreDetailsActionType.UPLOAD_STORE_PHOTOS:
            return {
                ...state,
                isLoadingUploadPhotos: true,
            };

        case StoreDetailsActionType.UPLOAD_STORE_PHOTOS_SUCCESS:
            return {
                ...state,
                isLoadingUploadPhotos: false,
                storePhotosInfo: [...action.payload, ...state.storePhotosInfo],
            };

        case StoreDetailsActionType.UPLOAD_STORE_PHOTOS_ERROR:
            return {
                ...state,
                isLoadingUploadPhotos: false,
            };

        case StoreDetailsActionType.DELETE_STORE_PHOTOS:
            return {
                ...state,
                isLoadingDeletePhotos: true,
            };

        case StoreDetailsActionType.DELETE_STORE_PHOTOS_SUCCESS:
            return {
                ...state,
                isLoadingDeletePhotos: false,
                storePhotosInfo: state.storePhotosInfo.filter((photo) => photo.name !== action.payload),
            };

        case StoreDetailsActionType.DELETE_STORE_PHOTOS_ERROR:
            return {
                ...state,
                isLoadingDeletePhotos: false,
            };

        case StoreDetailsActionType.DROP_ZONE_TOGGLE:
            return {
                ...state,
                isOpenDropZone: !state.isOpenDropZone,
            };

        case StoreDetailsActionType.FETCH_AI_CLASSIFICATIONS_HISTORY_START:
            return {
                ...state,
                isLoadingAiClassificationsHistory: true,
            };

        case StoreDetailsActionType.FETCH_AI_CLASSIFICATIONS_HISTORY_SUCCESS:
            return {
                ...state,
                isLoadingAiClassificationsHistory: false,
                aiClassificationsHistory: action.payload,
            };

        case StoreDetailsActionType.FETCH_AI_CLASSIFICATIONS_HISTORY_ERROR:
            return {
                ...state,
                isLoadingAiClassificationsHistory: false,
                aiClassificationsHistory: {
                    count: 0,
                    items: [],
                },
            };

        case StoreDetailsActionType.DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_START:
            return {
                ...state,
                isDownloadingAiClassificationsHistory: true,
            };

        case StoreDetailsActionType.DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_SUCCESS:
        case StoreDetailsActionType.DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_ERROR:
            return {
                ...state,
                isDownloadingAiClassificationsHistory: false,
            };

        case StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE:
            return {
                ...state,
                storeDetails: null,
                storeCashBoxesList: null,
                storeCashBoxesStats: null,
                coordinatesHistory: null,
                storePhotosInfo: null,
                inspectionsHistory: null,
                aiClassificationsHistory: null,
                currentHistoryTab: 0,
            };

        default:
            return state;
    }
}

export default storeDetailsReducer;
